// @givmo/org-console — the module-terms modal at Turn On (shell contract v3, T9).
//
// A module MAY bind an instrument for a class; when it does, turning it on presents the instrument
// once, at the organization level, and the switch refuses `enabled: true` until an acceptance
// exists. The client sequence the Apps page runs: Turn On → THIS modal (fetch the instrument, show
// it, Accept) → the accept operation → the switch write, or the provisioning hook's start.
//
// MINIMAL AND SUBORDINATED TO THE SWITCH, by ruling: one heading, one sentence saying what
// accepting does, the text in a scroll box, Cancel / Accept. No re-acceptance nag ever renders here
// — a newer version never turns a live module off and the main Terms' publish-and-notice rail
// carries meaningful changes.
//
// ⚠ THE REGISTRY SHIPS EMPTY. Every module of both classes answers `terms_state: 'none'` at birth,
// so this modal opens for nothing today; the mechanism is what lands. If it is opened for a module
// whose read then answers "no instrument" (the console's 404), it says so and offers to continue —
// the switch will flip freely, because there was nothing to accept.
//
// DATA CONTRACT: the `org-terms-*` test-id family.

import { Button, Dialog, ErrorMessage, testIdProp } from '@givmo/design-system';
import { isGivmoError } from '@givmo/api-client';
import type { OrgConsoleConfig } from './org-console-config';
import { useAcceptOrgModuleTerms, useOrgModuleTerms } from './use-org-console';

const PREFIX = 'org-terms';

export interface ModuleTermsDialogProps {
  config: OrgConsoleConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The module being turned on. `null` while closed. */
  module: string | null;
  /** Called once the acceptance has LANDED (or there was nothing to accept) — the switch proceeds. */
  onAccepted: () => void;
}

/**
 * The terms box's heading (D-CPO-79): the instrument named by its app, never by its version — the
 * acceptance records the version, and no one is shown it. Pinned in `copy-law.test.ts`.
 */
export function moduleTermsHeading(appLabel: string): string {
  return `${appLabel} terms`;
}

/**
 * The dialog's one line (D-CPO-79): what accepting does, true for an app with setup (Books) and one
 * without (Compliance). Pinned in `copy-law.test.ts`.
 */
export function moduleTermsLine(appLabel: string): string {
  return `${appLabel} has its own terms. Accept them once for your organization to turn ${appLabel} on. Admins added later won’t be asked again.`;
}

/** The instrument's text as paragraphs. Never HTML. */
function paragraphs(content: string | null): string[] {
  if (content === null) return [];
  return content
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

export function ModuleTermsDialog({
  config,
  open,
  onOpenChange,
  module,
  onAccepted,
}: ModuleTermsDialogProps) {
  const slug = module ?? '';
  const appLabel = module === null ? '' : config.vocab.moduleLabel(module);
  const terms = useOrgModuleTerms(config.orgRef, slug, open && module !== null);
  const accept = useAcceptOrgModuleTerms(config.orgRef, slug);
  const instrument = terms.data ?? null;
  const nothingToAccept = terms.isSuccess && instrument === null;
  const alreadyAccepted = instrument !== null && instrument.accepted;

  const proceed = () => {
    if (instrument === null || alreadyAccepted) {
      onAccepted();
      onOpenChange(false);
      return;
    }
    accept.mutate(
      { content_hash: instrument.content_hash },
      {
        onSuccess: () => {
          onAccepted();
          onOpenChange(false);
        },
        // A stale hash (409 `MODULE_TERMS_STALE`) means the copy on screen is not the current
        // version: reload it here and now, so the next Accept posts the hash the person has read.
        onError: (error) => {
          if (isGivmoError(error) && error.status === 409) void terms.refetch();
        },
      },
    );
  };

  const acceptError = accept.isError
    ? isGivmoError(accept.error) && accept.error.status === 409
      ? 'These terms changed while you were reading. They have been reloaded below — please read them again and accept.'
      : 'We couldn’t record your acceptance. Nothing was saved — please try again.'
    : null;

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !accept.isPending && onOpenChange(next)}>
      <Dialog.Content
        aria-label={moduleTermsHeading(appLabel)}
        className="flex flex-col gap-16"
        width={480}
        testId={`${PREFIX}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Turn on {appLabel}
        </Dialog.Title>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {moduleTermsLine(appLabel)}
        </p>

        {terms.isPending && (
          <p
            role="status"
            className="font-sans text-s13 text-text-tertiary"
            {...testIdProp(`${PREFIX}-loading`)}
          >
            Loading the terms…
          </p>
        )}

        {terms.isError && (
          <div className="flex flex-col items-start gap-8">
            <ErrorMessage testId={`${PREFIX}-read-error`}>
              We couldn’t load the terms just now.
            </ErrorMessage>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void terms.refetch()}
              testId={`${PREFIX}-retry`}
            >
              Try again
            </Button>
          </div>
        )}

        {nothingToAccept && (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
            {...testIdProp(`${PREFIX}-none`)}
          >
            No terms are registered for {appLabel}. There is nothing to accept.
          </p>
        )}

        {instrument !== null && (
          <div
            className="max-h-[40vh] overflow-y-auto rounded-lg border border-border-subtle bg-surface-sunken p-16"
            {...testIdProp(`${PREFIX}-body`)}
          >
            {/* The wire carries no title of its own: the instrument is named by its app. Never its
                version (D-CPO-79): the acceptance records it, and no one is shown it. */}
            <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              {moduleTermsHeading(appLabel)}
            </p>
            {paragraphs(instrument.content).map((text, index) => (
              <p
                key={index}
                className="mt-8 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary first:mt-0"
              >
                {text}
              </p>
            ))}
            {alreadyAccepted && (
              <p
                className="mt-8 font-sans text-s12 font-w500 tracking-givmo text-text-tertiary"
                {...testIdProp(`${PREFIX}-accepted`)}
              >
                Your organization has already accepted these terms.
              </p>
            )}
          </div>
        )}

        {acceptError !== null && (
          <ErrorMessage testId={`${PREFIX}-error`}>{acceptError}</ErrorMessage>
        )}

        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={accept.isPending}
            render={<Button appearance="saas" tone="secondary" size="md" className="max-sm:h-44" />}
            {...testIdProp(`${PREFIX}-cancel`)}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            className="max-sm:h-44"
            loading={accept.isPending}
            disabled={terms.isPending || terms.isError}
            onClick={proceed}
            testId={`${PREFIX}-accept`}
          >
            {nothingToAccept || alreadyAccepted ? 'Continue' : 'Accept and turn on'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
